import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Building2, Save, Trash2, Check, X, MessageCircle, DollarSign, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Demo mappings (same as backend)
const DEMO_LIST = [
  { subdomain: "smp", sector: "Micropigmentación" },
  { subdomain: "coaching", sector: "Coaching" },
  { subdomain: "limpieza", sector: "Limpieza B2B" },
  { subdomain: "gimnasio", sector: "Gimnasio" },
  { subdomain: "natural", sector: "Salud Natural" },
  { subdomain: "pizzeria", sector: "Pizzería" },
  { subdomain: "meals", sector: "Comidas Fitness" },
  { subdomain: "destilacion", sector: "Curso Destilación" },
  { subdomain: "contabilidad", sector: "SaaS Contabilidad" },
  { subdomain: "ventas", sector: "Sistema Ventas" },
  { subdomain: "solar", sector: "Energía Solar" },
  { subdomain: "reposteria", sector: "Curso Repostería" },
  { subdomain: "yoga", sector: "Retiro Yoga" },
  { subdomain: "agricultura", sector: "Agricultura" },
  { subdomain: "inmobiliaria", sector: "Inmobiliaria" },
  { subdomain: "hipnosis", sector: "Hipnosis" },
  { subdomain: "catering", sector: "Catering" },
  { subdomain: "pintura", sector: "Pintura" },
  { subdomain: "orangetheory", sector: "OrangeTheory" },
  { subdomain: "suplementos", sector: "Suplementos" },
  { subdomain: "viajes", sector: "Viajes" },
  { subdomain: "paleo", sector: "Alimentación Paleo" },
  { subdomain: "raywhite", sector: "Inmobiliaria Premium" },
  { subdomain: "reformas", sector: "Reformas" },
  { subdomain: "abogados", sector: "Abogados" },
];

export function DemoRentalManager() {
  const [configs, setConfigs] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/demo-rental`);
      const configMap = {};
      (res.data.configs || []).forEach(c => { configMap[c.subdomain] = c; });
      setConfigs(configMap);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (subdomain) => {
    const existing = configs[subdomain] || {};
    setForm({
      phone: existing.phone || '',
      email: existing.email || '',
      whatsapp: existing.whatsapp || '',
      business_name: existing.business_name || '',
      address: existing.address || '',
      is_rented: existing.is_rented || false,
      renter_name: existing.renter_name || '',
      monthly_price: existing.monthly_price || '',
      notes: existing.notes || ''
    });
    setEditing(subdomain);
  };

  const saveConfig = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/demo-rental/${editing}`, form);
      setConfigs(prev => ({ ...prev, [editing]: res.data }));
      toast.success('Configuración guardada');
      setEditing(null);
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async (subdomain) => {
    if (!window.confirm('¿Eliminar configuración?')) return;
    try {
      await axios.delete(`${API_URL}/api/demo-rental/${subdomain}`);
      setConfigs(prev => { const n = {...prev}; delete n[subdomain]; return n; });
      toast.success('Configuración eliminada');
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) return <div className="text-gray-400 p-4">Cargando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-white">Configuración Rank & Rent</h3>
          <p className="text-sm text-gray-400">Personaliza datos de contacto para cada landing demo</p>
        </div>
      </div>

      <div className="grid gap-3">
        {DEMO_LIST.map(demo => {
          const config = configs[demo.subdomain];
          const isEditing = editing === demo.subdomain;
          
          return (
            <div key={demo.subdomain} className={`bg-gray-800/50 border rounded-lg p-3 ${config?.is_rented ? 'border-emerald-500/50' : 'border-gray-700'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${config?.is_rented ? 'bg-emerald-500' : 'bg-gray-600'}`} />
                  <div>
                    <span className="font-medium text-white">{demo.subdomain}.lokalgrow.com</span>
                    <span className="text-gray-500 text-sm ml-2">({demo.sector})</span>
                  </div>
                  {config?.is_rented && (
                    <span className="px-2 py-0.5 bg-emerald-900/50 text-emerald-400 text-xs rounded">
                      Alquilado: {config.renter_name || 'Sin nombre'}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {!isEditing ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => startEdit(demo.subdomain)}>
                        Editar
                      </Button>
                      {config && (
                        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteConfig(demo.subdomain)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button size="sm" onClick={saveConfig} disabled={saving} className="bg-emerald-600">
                        <Save className="w-4 h-4 mr-1" /> Guardar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Quick view of config */}
              {!isEditing && config && (
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-400">
                  {config.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{config.phone}</span>}
                  {config.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{config.email}</span>}
                  {config.monthly_price && <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{config.monthly_price}€/mes</span>}
                </div>
              )}

              {/* Edit form */}
              {isEditing && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Teléfono</label>
                    <div className="flex items-center bg-gray-900 rounded border border-gray-700">
                      <Phone className="w-4 h-4 text-gray-500 ml-2" />
                      <input
                        type="text"
                        value={form.phone}
                        onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                        placeholder="+34 612 345 678"
                        className="w-full bg-transparent p-2 text-sm text-white outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Email</label>
                    <div className="flex items-center bg-gray-900 rounded border border-gray-700">
                      <Mail className="w-4 h-4 text-gray-500 ml-2" />
                      <input
                        type="email"
                        value={form.email}
                        onChange={e => setForm(f => ({...f, email: e.target.value}))}
                        placeholder="info@empresa.com"
                        className="w-full bg-transparent p-2 text-sm text-white outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">WhatsApp</label>
                    <div className="flex items-center bg-gray-900 rounded border border-gray-700">
                      <MessageCircle className="w-4 h-4 text-gray-500 ml-2" />
                      <input
                        type="text"
                        value={form.whatsapp}
                        onChange={e => setForm(f => ({...f, whatsapp: e.target.value}))}
                        placeholder="+34612345678"
                        className="w-full bg-transparent p-2 text-sm text-white outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Nombre Empresa</label>
                    <div className="flex items-center bg-gray-900 rounded border border-gray-700">
                      <Building2 className="w-4 h-4 text-gray-500 ml-2" />
                      <input
                        type="text"
                        value={form.business_name}
                        onChange={e => setForm(f => ({...f, business_name: e.target.value}))}
                        placeholder="Mi Empresa S.L."
                        className="w-full bg-transparent p-2 text-sm text-white outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Inquilino</label>
                    <div className="flex items-center bg-gray-900 rounded border border-gray-700">
                      <User className="w-4 h-4 text-gray-500 ml-2" />
                      <input
                        type="text"
                        value={form.renter_name}
                        onChange={e => setForm(f => ({...f, renter_name: e.target.value}))}
                        placeholder="Nombre del cliente"
                        className="w-full bg-transparent p-2 text-sm text-white outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Precio/mes (€)</label>
                    <div className="flex items-center bg-gray-900 rounded border border-gray-700">
                      <DollarSign className="w-4 h-4 text-gray-500 ml-2" />
                      <input
                        type="number"
                        value={form.monthly_price}
                        onChange={e => setForm(f => ({...f, monthly_price: e.target.value}))}
                        placeholder="99"
                        className="w-full bg-transparent p-2 text-sm text-white outline-none"
                      />
                    </div>
                  </div>
                  <div className="col-span-2 md:col-span-3 flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.is_rented}
                        onChange={e => setForm(f => ({...f, is_rented: e.target.checked}))}
                        className="w-4 h-4 rounded bg-gray-900 border-gray-700"
                      />
                      <span className="text-sm text-gray-300">Marcar como alquilado</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DemoRentalManager;
